/**
 * InitiativeFormDialog — Sheet Migration Wave 4a (AC-W4a-02)
 *
 * Create/edit sheet for an Initiative. Extracted from the inline
 * `CreateInitiativeDialog` in InitiativesPage.tsx and extended with edit mode.
 *
 * Uses FormSheet<InitiativeSnapshot> for dirty-state guard, discard banner,
 * sticky header, scrollable body, and opt-in URL routing.
 *
 * Fields (5):
 *   1. title (Input, required)
 *   2. domain (Select, required; disabled in edit mode — domain is immutable)
 *   3. start_date (DateInput, required)
 *   4. end_date (DateInput, required)
 *   5. description (Textarea, optional)
 *
 * Named "FormDialog" for parent API stability even though it renders a Sheet.
 *
 * PRD §9.3.6 + §12.3.9 J6.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { FormSheet } from '@/components/ui/FormSheet';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { DateInput } from '@/components/ui/DateInput';
import { Alert } from '@/components/ui/Alert';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import {
  useRorosInitiativeCreate,
  useRorosInitiativeUpdate,
} from '@/hooks/roros/artifacts';
import type { RorosInitiativeDTO, CreateInitiativePayload, UpdateInitiativePayload } from '@/types/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

type InitiativeSnapshot = {
  title: string;
  domainId: string;
  startDate: string;
  endDate: string;
  description: string;
};

function diffInitiativeSnapshots(
  base: InitiativeSnapshot,
  cur: InitiativeSnapshot,
): Partial<Record<keyof InitiativeSnapshot, boolean>> {
  return {
    title: base.title !== cur.title,
    domainId: base.domainId !== cur.domainId,
    startDate: base.startDate !== cur.startDate,
    endDate: base.endDate !== cur.endDate,
    description: base.description !== cur.description,
  };
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface InitiativeFormDialogProps {
  mode: 'create' | 'edit';
  isOpen: boolean;
  onClose: () => void;
  domains: RorosDomainDTO[];
  /** Populated in edit mode. */
  initiative?: RorosInitiativeDTO;
  /** URL routing pilot: when set, FormSheet syncs ?urlKey=urlId in the URL. */
  urlKey?: string;
  /** The id value written to the URL param. Required when urlKey is set. */
  urlId?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthFromNow(): string {
  return new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InitiativeFormDialog({
  mode,
  isOpen,
  onClose,
  domains,
  initiative,
  urlKey,
  urlId,
}: InitiativeFormDialogProps) {
  const createMutation = useRorosInitiativeCreate();
  const updateMutation = useRorosInitiativeUpdate();

  const activeDomains = useMemo(() => (domains ?? []).filter((d) => d.isActive), [domains]);

  // ─── Form state via useFormSnapshot ───────────────────────────────────────
  const { state, setField, baseline, current } = useFormSnapshot<InitiativeSnapshot>({
    initialFactory: () =>
      mode === 'edit' && initiative
        ? {
            title: initiative.title,
            domainId: initiative.domainId,
            startDate: initiative.startDate ? initiative.startDate.slice(0, 10) : today(),
            endDate: initiative.endDate ? initiative.endDate.slice(0, 10) : monthFromNow(),
            description: initiative.description ?? '',
          }
        : {
            title: '',
            domainId: activeDomains[0]?.id ?? '',
            startDate: today(),
            endDate: monthFromNow(),
            description: '',
          },
    isOpen,
    depKey: initiative?.id ?? null,
  });

  const { title, domainId, startDate, endDate, description } = state;

  // Re-sync default domain when activeDomains arrives async (create mode).
  useEffect(() => {
    if (mode === 'create' && !domainId && activeDomains[0]) {
      setField('domainId', activeDomains[0].id);
    }
  }, [mode, activeDomains, domainId, setField]);

  const [error, setError] = useState<string | null>(null);
  // Reset error on open.
  useEffect(() => {
    if (isOpen) setError(null);
  }, [isOpen, initiative?.id]);

  // ─── Validation & save ────────────────────────────────────────────────────
  const handleSave = () => {
    setError(null);

    if (!title.trim()) {
      setError(t('Title is required'));
      return;
    }
    if (!domainId) {
      setError(t('Domain is required'));
      return;
    }
    if (!startDate || !endDate) {
      setError(t('Start and end dates are required'));
      return;
    }
    if (startDate > endDate) {
      setError(t('End date must be on or after the start date'));
      return;
    }

    if (mode === 'edit' && initiative) {
      const payload: UpdateInitiativePayload = {
        title: title.trim(),
        description: description.trim() || undefined,
        start_date: startDate,
        end_date: endDate,
      };
      updateMutation.mutate(
        { domainId: initiative.domainId, id: initiative.id, payload },
        {
          onSuccess: () => {
            showToast.success(t('Initiative updated'));
            onClose();
          },
          onError: (err: any) => {
            setError(err?.message ?? t('Failed to update initiative'));
          },
        },
      );
    } else {
      const payload: CreateInitiativePayload = {
        title: title.trim(),
        description: description.trim() || undefined,
        start_date: startDate,
        end_date: endDate,
      };
      createMutation.mutate(
        { domainId, payload },
        {
          onSuccess: () => {
            showToast.success(t('Initiative created'));
            onClose();
          },
          onError: (err: any) => {
            setError(err?.message ?? t('Failed to create initiative'));
          },
        },
      );
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  const domainOptions = useMemo(
    () => activeDomains.map((d) => ({ value: d.id, label: d.name })),
    [activeDomains],
  );

  return (
    <FormSheet<InitiativeSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? t('Edit Initiative') : t('New Initiative')}
      mode={mode}
      baseline={baseline}
      current={current}
      diff={diffInitiativeSnapshots}
      onSubmit={handleSave}
      isSubmitting={isPending}
      submitLabel={mode === 'edit' ? t('Save') : t('Create Initiative')}
      submittingLabel={t('Saving…')}
      submitTestId="initiative-create-submit"
      width="md"
      urlKey={urlKey}
      urlId={urlId}
    >
      <div className="space-y-4">
        {error && (
          <Alert variant="error" title={t('Error')}>
            {error}
          </Alert>
        )}

        {/* 1. Title */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1" htmlFor="initiative-title">
            {t('Title')} <span className="text-destructive" aria-hidden>*</span>
          </label>
          <Input
            id="initiative-title"
            type="text"
            value={title}
            onChange={(e) => setField('title', e.target.value)}
            placeholder={t('e.g. Switch CRM vendor')}
            maxLength={255}
            required
            autoFocus
            data-testid="initiative-create-title"
          />
        </div>

        {/* 2. Domain */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1" htmlFor="initiative-domain">
            {t('Domain')} <span className="text-destructive" aria-hidden>*</span>
          </label>
          <Select
            id="initiative-domain"
            value={domainId}
            onChange={(e) => setField('domainId', e.target.value)}
            options={
              domainOptions.length > 0
                ? domainOptions
                : [{ value: '', label: t('No active domains') }]
            }
            disabled={mode === 'edit'}
            data-testid="initiative-create-domain"
          />
          {mode === 'edit' && (
            <p className="text-xs text-muted-foreground mt-1">
              {t('Domain cannot be changed after creation.')}
            </p>
          )}
        </div>

        {/* 3 & 4. Date range */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1" htmlFor="initiative-start">
              {t('Start Date')} <span className="text-destructive" aria-hidden>*</span>
            </label>
            <DateInput
              value={startDate}
              onChange={(iso) => setField('startDate', iso)}
              data-testid="initiative-create-start"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1" htmlFor="initiative-end">
              {t('End Date')} <span className="text-destructive" aria-hidden>*</span>
            </label>
            <DateInput
              value={endDate}
              onChange={(iso) => setField('endDate', iso)}
              data-testid="initiative-create-end"
            />
          </div>
        </div>

        {/* 5. Description */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1" htmlFor="initiative-description">
            {t('Description')}
          </label>
          <Textarea
            id="initiative-description"
            rows={3}
            value={description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder={t('What change does this initiative drive?')}
          />
        </div>
      </div>
    </FormSheet>
  );
}
